import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface CreatingClaimProps {
  /**
   * Current claim status. Drives the stage copy:
   *   - 'processing' → "Processing claim"
   *   - 'scrubbing'  → "Validating claim"
   *   - anything else falls back to the initial-create copy.
   */
  status?: string;
}

const STAGE_COPY: Record<string, { title: string; subtitle: string }> = {
  processing: {
    title: 'Processing claim',
    subtitle: 'Finishing the current claim operation.',
  },
  scrubbing: {
    title: 'Validating claim',
    subtitle: 'Running scrub rules to catch any issues before submission.',
  },
};

const DEFAULT_COPY = {
  title: 'Creating claim',
  subtitle:
    'We are getting your claim set up. We\u2019ll pre-populate information where we have it and recommend billing codes based on your session data.',
};

export const CreatingClaim: React.FC<CreatingClaimProps> = ({ status }) => {
  const copy = (status && STAGE_COPY[status]) || DEFAULT_COPY;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: 3,
      }}
    >
      <CircularProgress size={48} />
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: 420,
          animation: 'creatingClaimPulse 1.8s ease-in-out infinite',
          '@keyframes creatingClaimPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.65 },
          },
        }}
      >
        <Typography variant="h6" gutterBottom>
          {copy.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {copy.subtitle}
        </Typography>
      </Box>
    </Box>
  );
};
